import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { DialogModal } from "@pushpress/shared-components";
import { ButtonType } from "@pushpress/shared-components/build/components/buttons/Button";
import { useToggle } from "../../../hooks/state";

interface BaseOptions {
  confirmText?: string;
  confirmType?: ButtonType;
  cancelText?: string;
  onConfirm: () => Promise<void> | void;
}

interface ConfirmOptions extends BaseOptions {
  title: string;
}

interface DiscardConfirmOptions extends BaseOptions {
  title?: string;
}

interface ConfirmContextValue {
  showConfirm: (options: ConfirmOptions) => void;
  showDiscardConfirm: (options: DiscardConfirmOptions) => void;
}

interface ConfirmProviderProps {
  children?: ReactNode;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(
  undefined,
);

const ConfirmProvider = (props: ConfirmProviderProps) => {
  const { children } = props;

  const [dialogVisible, dialogActions] = useToggle(false);

  const [loading, loadingActions] = useToggle(false);

  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>();

  const showConfirm = useCallback(
    (options: ConfirmOptions) => {
      setConfirmOptions({
        title: options.title,
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        onConfirm: options.onConfirm,
      });
      dialogActions.on();
    },
    [dialogActions],
  );

  const showDiscardConfirm = useCallback(
    (options: DiscardConfirmOptions) => {
      setConfirmOptions({
        title: options.title || "Discard unsaved changes?",
        confirmText: options.confirmText || "Discard",
        cancelText: options.cancelText || "Go back",
        onConfirm: options.onConfirm,
      });
      dialogActions.on();
    },
    [dialogActions],
  );

  const handleConfirm = useCallback(async () => {
    loadingActions.on();
    try {
      await confirmOptions?.onConfirm();
    } finally {
      loadingActions.off();
    }
  }, [confirmOptions, loadingActions]);

  const contextValue = useMemo(() => {
    return { showConfirm, showDiscardConfirm };
  }, [showConfirm, showDiscardConfirm]);

  const {
    title,
    confirmText = "Ok",
    confirmType,
    cancelText = "Cancel",
  } = confirmOptions || {};

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      <DialogModal
        title={title}
        visible={dialogVisible}
        primaryBtnTitle={confirmText}
        secondaryBtnTitle={cancelText}
        primaryHandler={handleConfirm}
        secondaryHandler={dialogActions.off}
        primaryType={confirmType}
        closeModal={dialogActions.off}
        loading={loading}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const contextValue = useContext(ConfirmContext);
  if (!contextValue) {
    throw new Error("useConfirm needs to be used inside a ConfirmProvider.");
  }
  return contextValue;
};

export default ConfirmProvider;
